<script setup lang="ts">
const props = defineProps({
	page: {
		type: Number,
		default: 1,
	},
	totalPages: {
		type: Number,
		default: 1,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{
	(e: 'onPageChange', page: number): void
	(e: 'onDisabledPrev', value: boolean): void
	(e: 'onDisabledNext', value: boolean): void
}>();

const { isTablet } = useWindowResize();

const currentPage = computed({
	get: () => props.page,
	set: (page: number) => {
		emit('onPageChange', page);
	},
});

const isDisabledPrevButton = computed({
	get: () => currentPage.value === 1,
	set: (value: boolean) => {
		emit('onDisabledPrev', value);
	},
});

const isDisabledNextButton = computed({
	get: () => currentPage.value === props.totalPages,
	set: (value: boolean) => {
		emit('onDisabledNext', value);
	},
});

const isEmptyData = computed(() => props.totalPages === 0);

function updateCurrentPage(direction: string) {
	let newPage = currentPage.value;
	if (direction === 'increment' && !isDisabledNextButton.value) {
		newPage = currentPage.value + 1;
	} else if (direction === 'decrement' && !isDisabledPrevButton.value) {
		newPage = currentPage.value - 1;
	}

	emit('onPageChange', newPage);
}

function handleClickPrev() {
	updateCurrentPage('decrement');
}

function handleClickNext() {
	updateCurrentPage('increment');
}

defineExpose({
	handleClickPrev,
	handleClickNext,
});
</script>

<template>
  <div class="pagination-container">
    <VPagination
      v-if="!isTablet"
      v-model="currentPage"
      rounded="circle"
      :length="totalPages"
      :total-visible="6"
      :disabled="isLoading"
    >
      <template #prev>
        <BaseButton
          variant="link"
          color="gray"
          :disabled="isLoading || isDisabledPrevButton"
          @click="handleClickPrev"
        >
          <ArrowLeftIcon />
          <span>Previous</span>
        </BaseButton>
      </template>
      <template #next>
        <BaseButton
          variant="link"
          color="gray"
          :disabled="isLoading || isDisabledNextButton"
          @click="handleClickNext"
        >
          <span>Next</span>
          <ArrowRightIcon />
        </BaseButton>
      </template>
    </VPagination>
    <div
      v-else
      class="mobile-pagination"
    >
      <BaseButton
        variant="link"
        color="gray"
        size="sm"
        :disabled="isLoading || isDisabledPrevButton"
        @click="handleClickPrev"
      >
        <ArrowLeftIcon />
        <span>Previous</span>
      </BaseButton>
      <div
        v-if="!isEmptyData"
        class="pagination-info text-sm"
      >
        <p>Page <span>{{ page }}</span> of <span>{{ totalPages }}</span></p>
      </div>
      <BaseButton
        variant="link"
        color="gray"
        size="sm"
        :disabled="isLoading || isDisabledNextButton"
        @click="handleClickNext"
      >
        <span>Next</span>
        <ArrowRightIcon />
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pagination-container {
    margin-top: spacings-get(5);

    button {
      padding: 0;
    }

    .mobile-pagination {
      display: flex;
      justify-content: space-between;
      align-items: center;

      button {
        width: rem(90);
        display: flex;
        justify-content: flex-end;
      }

      .pagination-info span {
        @include fonts-weight(medium);
      }
    }
  }
</style>